import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveSession,
  setErrorManagerCode,
  ThematicAvailabilityMyMSC,
} from '@msc-b2c/shared-utils';
import { DiningAvailabilityError, DiningFlowEntryPoint } from '@msc-b2c/interfaces/enums';
import {
  CallDiningRoomReservationsInput,
  DiningRoomI,
  SlotThematicAvailabilityI,
  SlotThematicAvailabilityWithDate,
  ThematicAvailabilityRequest,
  ThematicAvailabilityResponse,
  UseDiningAvailabilityParams,
} from '@msc-b2c/interfaces/interfaces';
import { DINING_ERROR_CODES } from '../constants/diningErrorCodes';
import { buildDiningRoomDateAvailabilityKey } from '../utils/diningAvailabilityUtils';
import {
  createIssueCodeError,
  normalizeThematicResponse,
  shouldDisableReserveNowFromThematicIssueCode,
  toIssueCode,
} from '../utils/diningAvailabilityPolicy';
import { useDiningBootstrapSeed } from './useDiningBootstrapSeed';
import { usePaxEditBootstrapOverride } from './usePaxEditBootstrapOverride';

/**
 * Main availability orchestration hook for dining flow.
 *
 * End-to-end responsibility:
 * 1. Build bootstrap state from INT130/INT135 (seed rooms, slots, limits).
 * 2. Execute INT136 requests for validation and room/date availability index.
 * 3. Expose stable UI-facing state for loader/error/fallback behaviors.
 * 4. Enforce stale-request protection so old responses never override newer user selections.
 */

export const useDiningAvailability = ({
  shouldUseApiMocks,
  itineraryDays,
  seedDiningRoomCode,
  shopObsData,
  selectedPassengerIds,
  isLayerExcursionOpen,
  excursionObsCode,
  myAreaObsCode,
  entryPoint,
  persistedMaximumSlotsBookable,
  alreadyPurchasedObsCard,
}: UseDiningAvailabilityParams) => {
  const dispatch = useDispatch();
  // Availability state is centralized here so the drawer can remain an orchestration shell.
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<DiningAvailabilityError | undefined>();
  const [thematicSlotsByDiningRoomDate, setThematicSlotsByDiningRoomDate] = useState<
    Record<string, SlotThematicAvailabilityWithDate[]>
  >({});
  const [resolvedIsReserveNowDisabled, setResolvedIsReserveNowDisabled] = useState<boolean | null>(
    null
  );
  const [forceNoTimeslotsFallback, setForceNoTimeslotsFallback] = useState(false);
  const [hasResolvedInitialThematicAvailability, setHasResolvedInitialThematicAvailability] =
    useState(false);
  const lastAvailabilityRequestRef = useRef(0);

  /**
   * CART pax-edit helper:
   * - optional INT135 bootstrap override
   * - deduplicated prefetch API for edit click/confirm phases
   */
  const { paxEditBootstrapOverride, prefetchINT135DiningRoomMetadata } =
    usePaxEditBootstrapOverride({
      entryPoint,
      alreadyPurchasedObsCard,
      onDispatchAction: dispatch,
    });

  /**
   * Seed availability model sourced from INT130/INT135 depending on flow entry point.
   */
  const {
    bootstrapSource,
    bootstrapHydrationSignature,
    bootstrapMaximumSlotsBookable,
    bootstrapAvailableDiningRooms,
    bootstrapThematicSlots,
    bootstrapIsReserveNowDisabled,
  } = useDiningBootstrapSeed({
    entryPoint,
    alreadyPurchasedObsCard,
    persistedMaximumSlotsBookable,
    shopObsData,
    paxEditBootstrapOverride,
    itineraryDays,
    seedDiningRoomCode,
  });

  // Public values: INT130/INT135 owns display responsibility (thematicSlots from bootstrap)
  // until INT136.1 initial-entry validation fails; in that case force no-timeslots layout.
  const thematicSlots = forceNoTimeslotsFallback ? [] : bootstrapThematicSlots;
  const availableDiningRooms = bootstrapAvailableDiningRooms;
  const maximumSlotsBookable = bootstrapMaximumSlotsBookable;
  const isReserveNowDisabled = resolvedIsReserveNowDisabled ?? bootstrapIsReserveNowDisabled;

  useEffect(() => {
    // When the bootstrap payload changes, drop any previous INT136-derived overrides and go back to
    // the seed state computed from the current INT130/INT135 payload.
    setThematicSlotsByDiningRoomDate({});
    setResolvedIsReserveNowDisabled(null);
    setForceNoTimeslotsFallback(false);
    setHasResolvedInitialThematicAvailability(false);
    setAvailabilityError(undefined);
  }, [bootstrapHydrationSignature]);

  /**
   * Executes INT136 for either:
   * - initial room/date availability hydration, or
   * - slot confirmation request (date+time provided).
   *
   * Validation and safety:
   * - checks required context and passenger selection,
   * - guards against stale responses (last-request-wins),
   * - maps API response into both flat slots and room/date index.
   */
  const callDiningRoomReservations = useCallback(
    async ({
      diningRoomCode,
      date,
      time,
      shouldSetLoading = true,
    }: CallDiningRoomReservationsInput): Promise<SlotThematicAvailabilityI[]> => {
      // INT136 requires cruise and OBS context from the active session/card selection.
      const { cruiseId = '', bookingNumber = '' } = getActiveSession();
      const obsCode = (isLayerExcursionOpen ? excursionObsCode : myAreaObsCode) ?? '';

      if (!cruiseId || !obsCode) {
        throw createIssueCodeError(DINING_ERROR_CODES.MISSING_CONTEXT);
      }

      if (selectedPassengerIds.length === 0) {
        throw createIssueCodeError(DINING_ERROR_CODES.PASSENGERS_REQUIRED);
      }

      const requestPayload: ThematicAvailabilityRequest = {
        bookingNumber: String(bookingNumber),
        passengers: selectedPassengerIds.map((id) => ({ id })),
        ...(diningRoomCode || seedDiningRoomCode || date || time
          ? {
              diningRooms: [
                {
                  ...(diningRoomCode
                    ? { code: diningRoomCode }
                    : seedDiningRoomCode
                    ? { code: seedDiningRoomCode }
                    : {}),
                  ...(date ? { date } : {}),
                  ...(time ? { time } : {}),
                },
              ],
            }
          : {}),
      };

      // Last-request-wins guard to avoid outdated responses overriding newer selections.
      const requestId = lastAvailabilityRequestRef.current + 1;
      lastAvailabilityRequestRef.current = requestId;

      if (shouldSetLoading) {
        setIsLoadingAvailability(true);
      }
      setAvailabilityError(undefined);
      const isSlotSelectionRequest = Boolean(date && time);
      if (!isSlotSelectionRequest) {
        setForceNoTimeslotsFallback(false);
        setHasResolvedInitialThematicAvailability(false);
      }

      try {
        // INT136 call.
        const api = new ThematicAvailabilityMyMSC(shouldUseApiMocks, false, requestPayload, {
          cruiseId,
          obsCode,
        });

        const response: ThematicAvailabilityResponse = await api.sendData();
        const normalizedSlotsByDiningRoom = normalizeThematicResponse(
          response,
          itineraryDays,
          shouldUseApiMocks
        );

        const normalizedSlots = normalizedSlotsByDiningRoom.map(({ slot }) => slot);

        const normalizedSlotsByRoomDate = normalizedSlotsByDiningRoom.reduce<
          Record<string, SlotThematicAvailabilityWithDate[]>
        >((acc, { diningRoomCode, slot }) => {
          const roomDateKey = buildDiningRoomDateAvailabilityKey(diningRoomCode, slot.date);
          if (!roomDateKey) {
            return acc;
          }

          if (!acc[roomDateKey]) {
            acc[roomDateKey] = [];
          }

          acc[roomDateKey].push(slot);
          return acc;
        }, {});

        if (isSlotSelectionRequest) {
          const hasRequestedSlotAvailability = normalizedSlots.some(
            (slot) => slot.date === date && slot.time === time
          );

          if (!hasRequestedSlotAvailability) {
            throw createIssueCodeError(DINING_ERROR_CODES.UNAVAILABLE_SELECTION);
          }
        }

        if (!isSlotSelectionRequest) {
          setForceNoTimeslotsFallback(false);
          setThematicSlotsByDiningRoomDate(normalizedSlotsByRoomDate);
          setResolvedIsReserveNowDisabled(normalizedSlots.length === 0);
          setHasResolvedInitialThematicAvailability(true);
        }

        // INT136.1 validates slot availability but does NOT replace INT130/INT135 display.
        // Resolved state is used for room-date indexing during slot confirmation.
        // On failure, clear resolved state so UI falls back to INT130/INT135 slots.
        return normalizedSlots;
      } catch (error) {
        if (lastAvailabilityRequestRef.current === requestId) {
          const issueCode = toIssueCode(error);
          if (isSlotSelectionRequest && entryPoint === DiningFlowEntryPoint.CART) {
            dispatch(setErrorManagerCode(issueCode));
          }
          console.error('[DINING_AVAILABILITY] INT136 call failed', {
            issueCode,
            error: error instanceof Error ? error.message : String(error),
            isSlotSelectionRequest,
          });
          if (!isSlotSelectionRequest && shouldDisableReserveNowFromThematicIssueCode(issueCode)) {
            setResolvedIsReserveNowDisabled(true);
            setForceNoTimeslotsFallback(true);
            // Clear stale INT135 seed slots so all day/week buttons render as unavailable (AC5).
            setThematicSlotsByDiningRoomDate({});
          }
          if (!isSlotSelectionRequest) {
            setHasResolvedInitialThematicAvailability(true);
          }
          setAvailabilityError(undefined);
        }
        throw error;
      } finally {
        if (shouldSetLoading && lastAvailabilityRequestRef.current === requestId) {
          setIsLoadingAvailability(false);
        }
      }
    },
    [
      dispatch,
      entryPoint,
      excursionObsCode,
      isLayerExcursionOpen,
      itineraryDays,
      myAreaObsCode,
      seedDiningRoomCode,
      selectedPassengerIds,
      shouldUseApiMocks,
    ]
  );

  /**
   * Calls INT136.1 once for each restaurant code returned by INT130/INT135.
   * This is the correct initial-entry invocation: the service contract requires
   * one per-room request, never a parameterless request.
   *
   * IMPORTANT: INT136.1 is used only to VALIDATE/CONFIRM slot availability.
   * INT130/INT135 owns the slot display responsibility (thematicSlots always shows bootstrap data).
   * INT136.1 only sets `thematicSlotsByDiningRoomDate` for room-date lookups during slot selection.
   * On INT136.1 failure, clearing resolved state lets UI fall back to INT130/INT135 slots.
   *
   * Results are merged into room-date index for validation during slot confirmation.
   */
  const fetchInitialThematicAvailability = useCallback(async () => {
    setForceNoTimeslotsFallback(false);
    setHasResolvedInitialThematicAvailability(false);

    const fallbackDiningRoomsRaw =
      availableDiningRooms.length > 0
        ? availableDiningRooms
        : bootstrapSource?.diningRooms ??
          shopObsData?.diningRooms ??
          alreadyPurchasedObsCard?.diningRooms ??
          [];

    // INT130 types `diningRooms` as a single DiningRoomsOBSI object; INT135 uses DiningRoomI[].
    const fallbackDiningRooms: DiningRoomI[] = Array.isArray(fallbackDiningRoomsRaw)
      ? fallbackDiningRoomsRaw
      : fallbackDiningRoomsRaw
      ? [fallbackDiningRoomsRaw as DiningRoomI]
      : [];

    const targetDiningRooms: Array<DiningRoomI & { code: string }> = fallbackDiningRooms.filter(
      (room: DiningRoomI): room is DiningRoomI & { code: string } => Boolean(room?.code)
    );

    if (targetDiningRooms.length === 0) {
      setResolvedIsReserveNowDisabled(true);
      setThematicSlotsByDiningRoomDate({});
      setHasResolvedInitialThematicAvailability(true);
      return;
    }

    const { cruiseId = '', bookingNumber = '' } = getActiveSession();
    const obsCode = (isLayerExcursionOpen ? excursionObsCode : myAreaObsCode) ?? '';

    if (!cruiseId || !obsCode) {
      setResolvedIsReserveNowDisabled(true);
      setThematicSlotsByDiningRoomDate({});
      setHasResolvedInitialThematicAvailability(true);
      return;
    }

    if (selectedPassengerIds.length === 0) {
      setHasResolvedInitialThematicAvailability(true);
      return;
    }

    const requestId = lastAvailabilityRequestRef.current + 1;
    lastAvailabilityRequestRef.current = requestId;

    setIsLoadingAvailability(true);
    setAvailabilityError(undefined);

    try {
      const allResponses: ThematicAvailabilityResponse[] = await Promise.all(
        targetDiningRooms.map((room: DiningRoomI & { code: string }) =>
          new ThematicAvailabilityMyMSC(
            shouldUseApiMocks,
            false,
            {
              bookingNumber: String(bookingNumber),
              passengers: selectedPassengerIds.map((id) => ({ id })),
              diningRooms: [{ code: room.code }],
            },
            { cruiseId, obsCode }
          ).sendData()
        )
      );

      if (lastAvailabilityRequestRef.current !== requestId) return;

      const allNormalized = allResponses.flatMap((response: ThematicAvailabilityResponse) =>
        normalizeThematicResponse(response, itineraryDays, shouldUseApiMocks)
      );

      const mergedSlots = allNormalized.map(({ slot }) => slot);
      const mergedSlotsByRoomDate = allNormalized.reduce<
        Record<string, SlotThematicAvailabilityWithDate[]>
      >((acc, { diningRoomCode, slot }) => {
        const roomDateKey = buildDiningRoomDateAvailabilityKey(diningRoomCode, slot.date);
        if (!roomDateKey) return acc;
        if (!acc[roomDateKey]) acc[roomDateKey] = [];
        acc[roomDateKey].push(slot);
        return acc;
      }, {});

      setThematicSlotsByDiningRoomDate(mergedSlotsByRoomDate);
      setResolvedIsReserveNowDisabled(mergedSlots.length === 0);
      setForceNoTimeslotsFallback(false);
      setHasResolvedInitialThematicAvailability(true);
    } catch (error) {
      if (lastAvailabilityRequestRef.current === requestId) {
        const issueCode = toIssueCode(error);
        if (shouldDisableReserveNowFromThematicIssueCode(issueCode)) {
          setResolvedIsReserveNowDisabled(true);
          setForceNoTimeslotsFallback(true);
          setThematicSlotsByDiningRoomDate({});
        }
        setHasResolvedInitialThematicAvailability(true);
      }
      throw error;
    } finally {
      if (lastAvailabilityRequestRef.current === requestId) {
        setIsLoadingAvailability(false);
      }
    }
  }, [
    alreadyPurchasedObsCard?.diningRooms,
    availableDiningRooms,
    bootstrapSource?.diningRooms,
    excursionObsCode,
    isLayerExcursionOpen,
    itineraryDays,
    myAreaObsCode,
    selectedPassengerIds,
    shopObsData?.diningRooms,
    shouldUseApiMocks,
  ]);

  /**
   * Public contract consumed by DiningMultistepDrawer and reservation-selection hook.
   */
  return {
    thematicSlots,
    thematicSlotsByDiningRoomDate,
    availableDiningRooms,
    maximumSlotsBookable,
    isLoadingAvailability,
    hasResolvedInitialThematicAvailability,
    hasInitialAvailabilityServiceErrorFallback: forceNoTimeslotsFallback,
    availabilityError,
    isReserveNowDisabled,
    setAvailabilityError,
    callDiningRoomReservations,
    fetchInitialThematicAvailability,
    prefetchINT135DiningRoomMetadata,
  };
};
